import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rdEquipamentosService } from '@/services/quotes/rd-equipamentos';
import type {
  RdCoverageSelectionPayload,
  RdProposal,
} from '@/services/quotes/rd-equipamentos/types';
import { refetchRdProposalAndQuote } from './refetch-rd-proposal-and-quote.util';

type UseSetRdCoveragesParams = {
  proposalId: string;
  itemId: string;
};

/**
 * Autosave das coberturas de um item (Step 3, fluxo individual):
 * `rdEquipamentosService.setCoverages` faz o PUT da matriz completa do item
 * e, no sucesso, refetcha `['rd-proposal', proposalId]` — reflete force-adds
 * automáticos do backend (ex.: `despesa_salvamento` quando ausente da matriz
 * submetida, Task 7) e mantém os demais steps do wizard sincronizados.
 *
 * Diferente de `use-set-rd-fleet.hook.ts`, este hook não mapeia
 * `ValidationError` para um form TanStack — a matriz por item
 * (`coverage-matrix.component.tsx`) não usa `useAppForm`. O mapeamento de
 * erro por linha (`coverages.<index>.lmi`) e o erro de nível de accordion
 * (Básica obrigatória) são feitos ali, a partir do erro que `save` propaga
 * intacto (mesmo padrão try/catch de `use-set-rd-fleet.hook.ts`).
 *
 * No sucesso, refetcha `['rd-proposal', proposalId]` + `['rd-quote',
 * proposalId]` (via `refetchRdProposalAndQuote`) — editar coberturas muda o
 * prêmio calculado, e sem isso o Step Cotação ficava mostrando o valor
 * antigo até o usuário clicar em "Recalcular".
 */
export function useSetRdCoverages({
  proposalId,
  itemId,
}: UseSetRdCoveragesParams) {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    RdProposal,
    Error,
    RdCoverageSelectionPayload
  >({
    mutationKey: [
      'quotes',
      'rd-equipamentos',
      'set-coverages',
      proposalId,
      itemId,
    ],
    mutationFn: async (payload: RdCoverageSelectionPayload) => {
      const response = await rdEquipamentosService.setCoverages(
        proposalId,
        itemId,
        payload
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao salvar coberturas');
      }

      return response.data;
    },
  });

  const save = useCallback(
    async (payload: RdCoverageSelectionPayload): Promise<RdProposal> => {
      const result = await mutateAsync(payload);
      await refetchRdProposalAndQuote(queryClient, proposalId);
      return result;
    },
    [mutateAsync, queryClient, proposalId]
  );

  return { save, isPending };
}
